import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Box, Button, Header, SpaceBetween, Table } from '@amzn/awsui-components-react-v3/polaris';
import { RetryAlert } from '../../Common/ErrorMessages';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
// The base no-unused-vars rule does not see type-only uses.
// eslint-disable-next-line no-unused-vars
import { ApiError } from '../../../utils/serverErrors';
import * as pricingModels from './pricingModels';

export const PRICING_MODELS_ROUTE = '/settings/pricingModels';

interface PricingModelsListProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
  };
  navigate: (path: string) => void;
}

const PricingModelsList = ({ apiClient, navigate }: PricingModelsListProps): JSX.Element => {
  const [models, setModels] = useState<pricingModels.PricingModelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);

  const load = useCallback((): void => {
    setLoading(true);
    setLoadErrors([]);
    apiClient
      .httpGet('/pricingModels')
      .then(response => setModels(response as pricingModels.PricingModelSummary[]))
      .catch((error: ApiError) => {
        setModels([]);
        setLoadErrors(pricingModels.describeError(error));
      })
      .finally(() => setLoading(false));
  }, [apiClient]);

  useEffect(load, [load]);

  const createPath = `${PRICING_MODELS_ROUTE}/new`;

  const columnDefinitions = [
    {
      id: 'name',
      header: 'Name',
      cell: (model: pricingModels.PricingModelSummary): JSX.Element => (
        <Link to={`${PRICING_MODELS_ROUTE}/${encodeURIComponent(model.priceSchemeId)}`}>
          {model.name}
        </Link>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      cell: (model: pricingModels.PricingModelSummary): string => valueOrDash(model.description),
    },
    {
      id: 'latestChange',
      header: 'Latest change',
      cell: (model: pricingModels.PricingModelSummary): string =>
        pricingModels.latestChangeText(model.latestChangeSetAt, model.latestChangeSetAuthor),
    },
    {
      id: 'pendingChangeSetCount',
      header: 'Pending change sets',
      cell: (model: pricingModels.PricingModelSummary): number => model.pendingChangeSetCount,
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (model: pricingModels.PricingModelSummary): string =>
        convertDateTimePretty(model.createdAt),
    },
  ];

  return (
    <SpaceBetween size="m">
      {loadErrors.length > 0 && (
        <RetryAlert
          id="pricingModelsLoadError"
          header="Could not load pricing models"
          messages={loadErrors}
          retryId="retryPricingModels"
          onRetry={load}
        />
      )}
      <Table
        id="pricingModelsTable"
        items={models}
        loading={loading}
        loadingText="Loading pricing models"
        trackBy="priceSchemeId"
        columnDefinitions={columnDefinitions}
        header={
          <Header
            variant="h2"
            actions={
              <Button
                id="createPricingModel"
                variant="primary"
                href={createPath}
                onFollow={(event): void => {
                  event.preventDefault();
                  navigate(createPath);
                }}
              >
                Create pricing model
              </Button>
            }
          >
            Pricing models
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            {loadErrors.length > 0 ? '' : 'No pricing models.'}
          </Box>
        }
      />
    </SpaceBetween>
  );
};

PricingModelsList.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
};

export default PricingModelsList;
